import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ChevronDown } from 'lucide-react';

interface TimeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  selectedDate?: string; // Add selectedDate prop to determine Sunday logic
}

const TimeSelector = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select time',
  required = false,
  selectedDate,
}: TimeSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Define time slots based on whether selected date is Sunday
  const getTimeSlots = () => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      if (dayOfWeek === 0) {
        // Sunday
        return ['17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'];
      }
    }

    // Weekdays (Monday-Saturday)
    return [
      '12:00',
      '12:30',
      '13:00',
      '13:30',
      '14:00',
      '14:30',
      '15:00',
      '15:30',
      '16:00',
      '16:30',
      '17:00',
      '17:30',
      '18:00',
      '18:30',
      '19:00',
      '19:30',
      '20:00',
      '20:30',
      '21:00',
    ];
  };

  const timeSlots = getTimeSlots();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleTimeSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Format display time in 24-hour format (no AM/PM conversion)
  const formatDisplayTime = (time: string) => {
    return time; // Simply return the time as-is in 24-hour format
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 border border-neutral-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-neutral-50'
            : 'hover:border-red-300 cursor-pointer'
        } ${isOpen ? 'ring-2 ring-red-500 border-red-500' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select time"
        required={required}
      >
        <div className="flex items-center gap-3">
          <span
            className={`text-sm sm:text-base ${value ? 'text-neutral-900' : 'text-neutral-500'}`}
          >
            {value ? formatDisplayTime(value) : placeholder || 'Select time'}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-neutral-300 rounded-lg shadow-xl z-[99999] overflow-hidden min-w-[240px] sm:min-w-[280px] time-selector-dropdown"
            role="listbox"
            aria-label="Time options"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-red-800 to-red-700 text-white">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">Available Times</span>
              </div>
            </div>

            {/* Time Options */}
            <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100">
              <div className="p-4">
                <div className="space-y-1">
                  {timeSlots.map((time, index) => (
                    <motion.button
                      key={time}
                      type="button"
                      onClick={() => handleTimeSelect(time)}
                      className={`w-full flex items-center justify-between px-3 py-2 sm:px-4 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 time-slot ${
                        value === time
                          ? 'bg-red-800 text-white shadow-md'
                          : 'text-neutral-700 hover:bg-red-50 hover:text-red-800 border border-neutral-200'
                      }`}
                      role="option"
                      aria-selected={value === time}
                      whileHover={{
                        scale: value === time ? 1 : 1.02,
                        transition: { duration: 0.1 },
                      }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          delay: index * 0.02,
                          duration: 0.2,
                        },
                      }}
                    >
                      <span>{formatDisplayTime(time)}</span>
                      {value === time && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="ml-auto"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-200">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>All times in GMT</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimeSelector;
